import { useTranslation } from 'react-i18next';
import type { Delivery, DeliveryBoardStatus, Supplier } from '@interfaces/ILogistics';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { CalendarOrderCard } from './CalendarOrderCard';
import { dayLabel } from './calendar.labels';
import styles from './CalendarRail.module.css';

interface ICalendarRailProps {
  day: string;
  dayOrders: Delivery[];
  overdue: Delivery[];
  undated: Delivery[];
  statuses: DeliveryBoardStatus[];
  suppliers: Supplier[];
  tz: string;
  today: string;
  onOpenDelivery: (delivery: Delivery) => void;
}

/** The side column: the picked day, then the orders a month's grid can bury —
 *  the late ones and the ones with no date to sit on. */
export function CalendarRail({ day, dayOrders, overdue, undated, statuses, suppliers, tz, today, onOpenDelivery }: ICalendarRailProps) {
  const { t, i18n } = useTranslation();
  const heading = dayLabel(day, i18n.language);
  const cards = (orders: Delivery[]) => orders.map((delivery) => (
    <CalendarOrderCard
      key={delivery.ref}
      delivery={delivery}
      statuses={statuses}
      suppliers={suppliers}
      tz={tz}
      today={today}
      onOpen={onOpenDelivery}
    />
  ));

  return (
    <ScrollArea className={styles.rail} viewportClassName={styles.content}>
      <section aria-label={heading}>
        <h3 className={styles.title}>{heading} <em>{dayOrders.length}</em></h3>
        {dayOrders.length > 0 ? cards(dayOrders) : <p className={styles.empty}>{t('logistics.calendar.noDeliveries')}</p>}
      </section>
      {overdue.length > 0 ? (
        <section aria-label={t('logistics.calendar.overdue')}>
          <h3 className={`${styles.title} ${styles.titleLate}`}>{t('logistics.calendar.overdue')} <em>{overdue.length}</em></h3>
          {cards(overdue)}
        </section>
      ) : null}
      {undated.length > 0 ? (
        <section aria-label={t('logistics.calendar.undated')}>
          <h3 className={styles.title}>{t('logistics.calendar.undated')} <em>{undated.length}</em></h3>
          {cards(undated)}
        </section>
      ) : null}
    </ScrollArea>
  );
}
